import { Button, CloseButton, Select, Skeleton, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { Environment } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import VariableRow from '~/components/settings/variables/VariableRow';
import VariableForm from '~/components/settings/variables/VariableForm';
import { ENVIRONMENT_NAMES } from '~/common/environment/names';
import { IconFileDownload, IconSearch, IconSortDescending } from '@tabler/icons-react';
import clsx from 'clsx';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { downloadFile } from '~/logic/file/download';

export const SENSITIVE_VARIABLE_VALUE = '••••••••';

const SORT_BY_OPTIONS = [
  { value: 'updatedAt', label: 'Last Updated' },
  { value: 'name', label: 'Name' },
  { value: 'environment', label: 'Environment' },
] as const;

type SortBy = (typeof SORT_BY_OPTIONS)[number]['value'];

export default function Variables() {
  const projectId = useProjectId();
  const { orgMetadata } = useAuthData();
  const { data: variables = [], isLoading: isLoadingVariables } = api.variable.list.useQuery({ projectId: projectId! });
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [nameFilter, setNameFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('updatedAt');
  const environmentScopedEnabled = !!orgMetadata?.environmentScopedVariablesEnabled;
  const sortByOptions =
    environmentScopedEnabled ? SORT_BY_OPTIONS : SORT_BY_OPTIONS.filter((option) => option.value !== 'environment');

  // If environment scoped is disabled, selected environment should always be null and sort by should be updatedAt if it was environment
  // (since all vars will be in ALL environment)
  useEffect(() => {
    if (!environmentScopedEnabled) {
      setSelectedEnvironment(null);
      if (sortBy === 'environment') {
        setSortBy('updatedAt');
      }
    }
  }, [environmentScopedEnabled, sortBy]);

  const filteredVariables = useMemo(
    () =>
      variables
        // Filter by environment
        .filter((v) => !environmentScopedEnabled || !selectedEnvironment || v.environment === selectedEnvironment)
        // Filter by name
        .filter((v) => !nameFilter.trim() || v.name.toLowerCase().includes(nameFilter.toLowerCase()))
        // Sort by selected option
        .sort((a, b) =>
          sortBy === 'name' ? a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          : sortBy === 'updatedAt' ? new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          : sortBy === 'environment' ? a.environment.localeCompare(b.environment)
          : 0,
        ),
    [variables, selectedEnvironment, nameFilter, sortBy, environmentScopedEnabled],
  );

  const handleDownloadEnv = () => {
    if (filteredVariables.length === 0) {
      return;
    }

    const lines: string[] = [];
    for (const variable of filteredVariables) {
      // Sanitize variable name: replace any problematic characters with underscores
      const safeName = variable.name.replace(/[^a-zA-Z0-9_]/g, '_');

      if (variable.sensitive) {
        lines.push(`# ${safeName} - SENSITIVE (value not exported)`);
        lines.push(`${safeName}=`);
      } else {
        const rawValue = typeof variable.value === 'string' ? variable.value : JSON.stringify(variable.value);
        // Quote values that contain special characters and escape internal quotes
        const needsQuoting = /[\n\r#="'\s\\]/.test(rawValue);
        const value = needsQuoting ? `"${rawValue.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"` : rawValue;
        lines.push(`${safeName}=${value}`);
      }
    }

    const content = lines.join('\n');

    downloadFile(content, { filename: '.env', type: 'text/plain' });
    notifications.show({ message: `Downloaded ${filteredVariables.length} variables`, color: 'green' });
  };

  return !projectId ?
      <div className="flex flex-col space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">Please select a project to manage variables.</p>
      </div>
    : <div className="flex max-w-3xl flex-col space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Variables allow you to store and manage reusable values across your tests.
        </p>
        <Skeleton visible={isLoadingVariables}>
          <VariableForm
            variables={variables}
            environmentScopedEnabled={environmentScopedEnabled}
          />
          <div className="mt-4">
            <div className="mb-4 text-lg font-medium">
              <div className="mb-2 text-base font-bold">Environment Variables</div>
              <div className="mb-4 flex justify-between gap-4">
                <div className="flex gap-4">
                  <TextInput
                    leftSection={<IconSearch size={16} />}
                    placeholder="Search variables..."
                    value={nameFilter}
                    onChange={(event) => setNameFilter(event.currentTarget.value)}
                    className="min-w-64"
                    rightSection={
                      <CloseButton
                        onClick={() => setNameFilter('')}
                        className={clsx('cursor-pointer', !nameFilter && 'hidden')}
                        size="sm"
                        variant="transparent"
                      />
                    }
                  />
                  {!!environmentScopedEnabled && (
                    <Select
                      placeholder="Filter by environment"
                      className="w-48"
                      value={selectedEnvironment || null}
                      onChange={(value) => setSelectedEnvironment(value ? (value as Environment) : null)}
                      data={Object.entries(ENVIRONMENT_NAMES)
                        .filter(([environment]) => environment !== 'NONE')
                        .map(([environment, environmentName]) => ({
                          value: environment,
                          label: environmentName,
                        }))}
                      allowDeselect
                      clearable
                    />
                  )}
                </div>
                <div className="flex gap-2">
                  <Select
                    leftSection={<IconSortDescending size={16} />}
                    placeholder="Sort by"
                    value={sortBy}
                    onChange={(value) => setSortBy(value as SortBy)}
                    data={sortByOptions}
                    className="w-40"
                  />
                  <Button
                    variant="outline"
                    leftSection={<IconFileDownload size={16} />}
                    onClick={handleDownloadEnv}
                    disabled={filteredVariables.length === 0}
                  >
                    Download .env
                  </Button>
                </div>
              </div>
            </div>
            {variables.length === 0 ?
              <div className="pt-2">
                <p className="text-sm text-gray-500">No environment variables have been added yet</p>
              </div>
            : filteredVariables.length > 0 ?
              <div>
                {filteredVariables.map((variable) => (
                  <VariableRow
                    key={`${variable.name}-${variable.environment}`}
                    projectId={projectId}
                    variable={variable}
                    existingVariables={variables}
                    environmentScopedEnabled={environmentScopedEnabled}
                  />
                ))}
              </div>
            : <div className="pt-2">
                <p className="text-sm text-gray-500">
                  No environment variables have been added for{' '}
                  {selectedEnvironment ? ENVIRONMENT_NAMES[selectedEnvironment] : 'this'} environment
                </p>
              </div>
            }
          </div>
        </Skeleton>
      </div>;
}
